//ScrollStrip.tsx — the dock's scroll box shared by every carousel: edge fades plus arrow buttons

import type { ReactNode } from 'react'
import { ChevronIcon } from './icons'
import { useEdgeFade } from './useEdgeFade'

interface Props {
	label: string
	multiselectable?: boolean
	//Re-measures the fades and arrows when the number of items changes.
	itemCount: number
	children: ReactNode
}

//Rendered as siblings inside the dock, not inside the scroll box, so the arrows stay put while
//the items move. They only appear when something is out of view, and dim at the matching end.
export function ScrollStrip({ label, multiselectable = false, itemCount, children }: Props) {
	const fade = useEdgeFade(itemCount)

	return (
		<>
			{fade.overflows && (
				<button
					type="button"
					className="ghost icon-only strip-arrow"
					aria-label={`Scroll ${label} left`}
					disabled={fade.atStart}
					onClick={() => fade.page(-1)}
				>
					<ChevronIcon direction="left" />
				</button>
			)}
			<div
				ref={fade.ref}
				className={`strip ${fade.atStart ? 'at-start' : ''} ${fade.atEnd ? 'at-end' : ''}`}
				role="listbox"
				aria-label={label}
				aria-multiselectable={multiselectable || undefined}
			>
				{/* Inner track so `margin: auto` can centre a catalogue that does not fill the width.
				    justify-content would clip the first tile once it does overflow. */}
				<div className="strip-track">{children}</div>
			</div>
			{fade.overflows && (
				<button
					type="button"
					className="ghost icon-only strip-arrow"
					aria-label={`Scroll ${label} right`}
					disabled={fade.atEnd}
					onClick={() => fade.page(1)}
				>
					<ChevronIcon direction="right" />
				</button>
			)}
		</>
	)
}
